import React from 'react';
import {
  Button,
  Combobox,
  FieldSet,
  FormField,
  Heading,
  Input,
  Label,
  LabeledControl,
  Modal,
  Paragraph,
  Switch,
} from '@zillow/constellation-10';
import Cookies from 'js-cookie';
interface DevCookieControlsProps {
  devModalOpen: boolean;
  setDevModalOpen: (open: boolean) => void;
}

interface DevCookieToggles {
  ZRM_MOCK_USER_ENABLED: boolean;
}

interface DevCookieValues {
  ZRM_MOCK_USER_EMAIL?: string;
  ZRM_MOCK_USER_IMPERSONATOR_TOKEN?: string;
  ZRM_MOCK_USER_IMPERSONATOR_EMAIL?: string;
}

interface MockedUser {
  email: string;
  token: number;
  comment: string;
}

export const ZRM_MOCK_USERS: Array<MockedUser> = [
  {
    email: 'rocket_ship_user@zillow.com',
    token: 27183,
    comment: 'Single family feed listings',
  },
  {
    email: 'mfh_rocket_ship_user@zillow.com',
    token: 66261,
    comment: 'Multifamily feed listings',
  },
  {
    email: 'mfh_user@zillow.com',
    token: 31416,
    comment: 'Multifamily customer',
  },
  {
    email: 'company_admin@zillow.com',
    token: 123456,
    comment: 'Company admin for user management',
  },
  {
    email: 'nucleus_user@zillow.com',
    token: 12345,
    comment: 'Single family paid inclusion',
  },
  {
    email: 'single_family_legacy@zillow.com',
    token: 222222,
    comment: 'Old single family legacy user',
  },
  {
    email: 'flat_fee_rocket_ship_user@zillow.com',
    token: 975312,
    comment: 'Single family with flat fee product',
  },
  {
    email: 'mockeduser@zillow.com',
    token: 1111111111,
    comment: 'Has some posted listings',
  },
  {
    email: 'rentalrevenueplatform@zillow.com',
    token: 111213,
    comment: 'To test multifamily packages',
  },
];

const DevCookieControls = ({ devModalOpen, setDevModalOpen }: DevCookieControlsProps) => {
  const [devCookieToggles, setDevCookieToggles] = React.useState<DevCookieToggles>({
    ZRM_MOCK_USER_ENABLED: Boolean(Cookies.get('ZRM_MOCK_USER_ENABLED') === 'true'),
  });

  const [devCookieValues, setDevCookieValues] = React.useState<DevCookieValues>({
    ZRM_MOCK_USER_EMAIL: Cookies.get('ZRM_MOCK_USER_EMAIL'),
    ZRM_MOCK_USER_IMPERSONATOR_TOKEN: Cookies.get('ZRM_MOCK_USER_IMPERSONATOR_TOKEN'),
    ZRM_MOCK_USER_IMPERSONATOR_EMAIL: Cookies.get('ZRM_MOCK_USER_IMPERSONATOR_EMAIL'),
  });

  const handleToggleChange = (name: string, value: string | boolean) => {
    setDevCookieToggles({
      ...devCookieToggles,
      [name]: value,
    });
    Cookies.set(name, String(value), {
      // @ts-ignore
      sameSite: 'none',
      secure: true,
    });
  };

  const handleValueUpdate = (name: string, value: string) => {
    setDevCookieValues((oldValues) => ({
      ...oldValues,
      [name]: value,
    }));
    Cookies.set(name, value, {
      // @ts-ignore
      sameSite: 'none',
      secure: true,
    });
  };

  const renderPresetUsers = () => {
    const comboboxOptions = ZRM_MOCK_USERS.map((user) => ({
      value: user.email,
      label: user.email,
      meta: user.comment,
    }));
    return (
      <Combobox
        options={comboboxOptions}
        placeholder="Choose a mock user"
        freeForm={false}
        onOptionSelect={(option) => {
          if (typeof option === 'object' && option !== null && 'value' in option) {
            handleValueUpdate('ZRM_MOCK_USER_EMAIL', option.value as string);
          }
        }}
        value=""
      />
    );
  };

  const renderToggles = (toggles: Array<keyof DevCookieToggles>) => {
    return (
      <FieldSet size="lg">
        {toggles.map((feature) => {
          return (
            <LabeledControl
              key={feature}
              controlId={`SwitchGroup-${feature}`}
              label={<Label>{feature}</Label>}
              control={
                <Switch
                  checked={devCookieToggles[feature]}
                  onChange={() => handleToggleChange(feature, !devCookieToggles[feature])}
                />
              }
            />
          );
        })}
      </FieldSet>
    );
  };

  const renderInputs = (inputs: Array<keyof DevCookieValues>) =>
    inputs.map((name) => (
      <FormField
        css={{ marginBlock: '400', display: 'flex', alignItems: 'center' }}
        key={name}
        label={<Label css={{ flexBasis: '70%' }}>{name}</Label>}
        control={
          <Input
            value={devCookieValues[name]}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleValueUpdate(name, e.target.value)}
          />
        }
      />
    ));

  return (
    <Modal
      size="lg"
      open={devModalOpen}
      onOpenChange={(open) => !open && setDevModalOpen(false)}
      header={<Heading level={1}>Dev cookie manager</Heading>}
      body={
        <>
          <Paragraph css={{ marginBlock: '600' }}>
            Local development requires a mocked user. Please turn on to simulate login
          </Paragraph>
          {renderToggles(['ZRM_MOCK_USER_ENABLED'])}
          {renderPresetUsers()}
          {renderInputs([
            'ZRM_MOCK_USER_EMAIL',
            'ZRM_MOCK_USER_IMPERSONATOR_TOKEN',
            'ZRM_MOCK_USER_IMPERSONATOR_EMAIL',
          ])}
        </>
      }
      footer={
        <Button tone="brand" emphasis="filled" onClick={() => window.location.reload()}>
          Close and refresh
        </Button>
      }
    />
  );
};

export default DevCookieControls;
